import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * ResourceCard — a curated next-step card for the Home Page "Keep Exploring" section
 * and other resource surfaces. Designed to feel like an invitation, not a blog tile.
 *
 * Props:
 *  - category:     Optional short label (e.g. "Assessment", "Newsletter", "Story")
 *  - title:        Card headline
 *  - description:  Single-sentence supporting copy
 *  - ctaLabel:     Text on the CTA
 *  - href:         Destination. If starts with "http", opens in a new tab.
 *                  If omitted, the card renders as disabled (uses ctaLabel as-is).
 *  - badge:        Optional "Coming July" style badge. Displayed top-right.
 *  - disabled:     Force-disable the CTA even if href is provided.
 *  - testid:       data-testid root.
 */
export default function ResourceCard({
  category,
  title,
  description,
  ctaLabel,
  href,
  badge,
  disabled = false,
  testid,
}) {
  const isDisabled = disabled || !href;
  const isExternal = href && /^https?:/i.test(href);

  const cta = (
    <span className="inline-flex items-center gap-2 text-sm tracking-wide">
      {ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
    </span>
  );

  return (
    <article
      className={`relative bg-[#FDFBF7] border border-[#0A192F]/10 p-8 flex flex-col ${
        isDisabled ? "opacity-90" : "hover:border-[#0A192F]/30 transition-colors"
      }`}
      data-testid={testid}
    >
      {badge && (
        <span
          className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.2em] text-[#52796F] border border-[#52796F]/40 px-2.5 py-1 rounded-full"
          data-testid={`${testid}-badge`}
        >
          {badge}
        </span>
      )}

      {category && (
        <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
          {category}
        </div>
      )}

      <h3 className="mt-4 font-serif-display text-2xl text-[#0A192F] leading-snug">
        {title}
      </h3>

      <p className="mt-4 text-[#4A5568] leading-relaxed flex-1">
        {description}
      </p>

      <div className="mt-8">
        {isDisabled ? (
          <span
            className="inline-flex items-center gap-2 text-sm tracking-wide text-[#52796F]"
            data-testid={`${testid}-cta-disabled`}
          >
            {ctaLabel}
          </span>
        ) : isExternal ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-[#0A192F] border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
            data-testid={`${testid}-cta`}
          >
            {cta}
          </a>
        ) : (
          <Link
            to={href}
            className="text-[#0A192F] border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
            data-testid={`${testid}-cta`}
          >
            {cta}
          </Link>
        )}
      </div>
    </article>
  );
}
